import { HUGERTE_SCRIPT_SRC } from '@hugerte/hugerte-angular';
import { WORKFLOW_AUTHORING_EDITOR_OPTIONS } from '@shared/workflows/authoring';
import { registerLocaleData, DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { provideAdminDateAdapter } from './common/admin-date-adapter';
import {
  inject,
  LOCALE_ID,
  isDevMode,
  NgModule,
  provideAppInitializer,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import {
  createInterceptorCondition,
  IncludeBearerTokenCondition,
  includeBearerTokenInterceptor,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  provideKeycloak,
} from 'keycloak-angular';
import { interceptorProviders } from './core/interceptors/interceptors';
import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import { SideNavComponent } from './side-nav/side-nav.component';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RealmsOverviewComponent } from './realms/realms-overview/realms-overview.component';
import { InquiriesOverviewComponent } from './inquiries/inquiries-overview/inquiries-overview.component';
import { MenuBarComponent } from './menu-bar/menu-bar.component';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { HeadingContainerComponent } from './common/heading-container/heading-container.component';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { environment } from '../environments/environment';
import { ApiModule, Configuration, ConfigurationParameters } from '../api/gen';
import { CreateRealmDialogComponent } from './realms/dialogs/create-realm-dialog/create-realm-dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DebugComponent } from './debug/debug.component';
import { StaticTemplateOverviewComponent } from './templates/static-template-overview/static-template-overview.component';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { DeleteRealmDialogComponent } from './realms/dialogs/delete-realm-dialog/delete-realm-dialog.component';
import { BrandingColorService } from './core/services/branding-color.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { RealmService } from './auth/realm.service';
import { BackButtonComponent } from './common/back-button/back-button.component';
import { BackendUnavailableComponent } from './backend-unavailable/backend-unavailable.component';

registerLocaleData(localeDe);

export function apiConfigFactory(): Configuration {
  const params: ConfigurationParameters = {
    basePath: environment.apiPath,
  };
  return new Configuration(params);
}

const escapedApiPath = environment.apiPath.replace(
  /[.*+?^${}()|[\]\\]/g,
  '\\$&',
);
const apiPathCondition =
  createInterceptorCondition<IncludeBearerTokenCondition>({
    urlPattern: new RegExp(`^${escapedApiPath}(/.*)?$`, 'i'),
  });

@NgModule({
  declarations: [
    AppComponent,
    SideNavComponent,
    RealmsOverviewComponent,
    InquiriesOverviewComponent,
    CreateRealmDialogComponent,
    DebugComponent,
    StaticTemplateOverviewComponent,
    DeleteRealmDialogComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    ApiModule.forRoot(apiConfigFactory),
    BrowserModule,
    AppRoutingModule,
    MatSidenavModule,
    MatListModule,
    BrowserAnimationsModule,
    MatToolbarModule,
    MatButtonModule,
    MatCardModule,
    MatTableModule,
    MatDialogModule,
    MatSnackBarModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    FormsModule,
    MatIconModule,
    MatSelectModule,
    MatTabsModule,
    HeadingContainerComponent,
    MenuBarComponent,
    BackButtonComponent,
    BackendUnavailableComponent,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerImmediately',
    }),
  ],
  providers: [
    { provide: HUGERTE_SCRIPT_SRC, useValue: 'assets/hugerte/hugerte.min.js' },
    {
      provide: WORKFLOW_AUTHORING_EDITOR_OPTIONS,
      useFactory: () =>
        inject(LOCALE_ID).toLowerCase().startsWith('de')
          ? { language: 'de', language_url: '/assets/hugerte/langs/de.js' }
          : { language: 'en' },
    },
    { provide: LOCALE_ID, useValue: 'de-DE' },
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: { dateFormat: 'dd.MM.yyyy' },
    },
    provideAdminDateAdapter(),
    {
      provide: 'BrandingColorApi',
      useClass: BrandingColorService,
    },
    interceptorProviders,
    provideKeycloak({
      config: {
        url: environment.keycloak.url,
        realm: environment.keycloak.realm,
        clientId: environment.keycloak.clientId,
      },
      initOptions: {
        onLoad: 'login-required',
        checkLoginIframe: false,
      },
    }),
    provideAppInitializer(() =>
      inject(RealmService).resolve(window.location.hostname),
    ),
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [apiPathCondition],
    },
    provideHttpClient(
      withXhr(),
      withInterceptors([includeBearerTokenInterceptor]),
      withInterceptorsFromDi(),
    ),
  ],
})
export class AppModule {}
